<template>
  <v-app light id="app">
    <div v-if="loggedIn">
      <v-navigation-drawer fixed clipped app v-model="drawer">
          <v-list>
              <router-link to="/websitesoverview">
                  <v-list-item v-if="userIsAdministrator">
                    <v-list-item-action>
                      <v-icon>computer</v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-subtitle>
                           {{ $t('Websites')}}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
              </router-link>

              <router-link to="/usersoverview">
                <v-list-item v-if="userIsAdministrator">
                  <v-list-item-action>
                    <v-icon>account_circle</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-subtitle>
                         {{ $t('Users')}}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </router-link>

              <v-divider v-if="userIsAdministrator" />

              <v-list-item>
                <v-list-item-action>
                  <v-icon class="mt-4 mb-3">computer</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                    <!-- Show dropdown when there are multiple websites -->
                    <template v-if="websites">
                      <v-select v-if="websites.length > 1"
                         v-bind:items="websites"
                         :value="selectedWebsite"
                         :label="$t('Select a website')"
                         single-line
                         menu-props="bottom"
                         item-value="_id"
                         item-text="Name"
                         @change="setSelectedWebsite"
                         id="websiteSelect"
                      ></v-select>
                      <!-- Show label when there is only one website -->
                      <v-list-item-subtitle class="mt-2" v-if="websites.length == 1">
                          {{ websites[0].Name }}
                      </v-list-item-subtitle>
                    </template>
                </v-list-item-content>
              </v-list-item>

              <template v-if="selectedWebsite && currentLanguage">
                <template v-for="page in selectedWebsiteSortedPages">
                  <template v-if="page.Enabled && !page.IsSubPage">
                    <router-link  :key="page._id.$oid" :to="{ name: 'editPage', params: { id: page._id.$oid }}">
                      <v-list-item class="ml-1">
                        <v-list-item-content>
                          <v-list-item-subtitle v-if="page.Name[currentLanguage].length == 0">
                            {{page.Name[Object.keys(page.Name)[0]]}}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle v-else>
                            {{page.Name[currentLanguage]}}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </router-link>
                    <div :key="page._id.$oid" class="sub-pages-container" v-if="page.SubPages.length > 0">
                      <template v-for="subPage in page.SubPages">
                        <router-link :key="subPage.$oid" :to="{ name: 'editPage', params: { id: subPage.$oid }}">
                          <v-list-item class="ml-1">
                            <v-list-item-subtitle class="ml-4 pt-1 pb-1">
                              {{ getNameFromPageId(subPage.$oid)}}
                            </v-list-item-subtitle>
                          </v-list-item>
                        </router-link>
                      </template>
                    </div>
                  </template>
                </template>
              </template>
            </v-list>
      </v-navigation-drawer>

      <v-app-bar color="primary" id="toolbar" clipped-left fixed dark app>
        <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>

        <v-toolbar-title class="mr-5">
          <span class="hidden-xs-only">
            <router-link to="/">
              <img id="logo" :src="serverUrl+'/images/'+logo" />
            </router-link>
          </span>
        </v-toolbar-title>

        <div class="d-flex align-center" style="margin-left: auto">
          <languageSelector :whiteColors="true" />

          <v-btn outlined fab small class="mr-5" @click.stop="toggleSearchDrawer()">
            <v-icon>search</v-icon>
          </v-btn>

          <v-btn v-on:click='logOut' :loading="loggingOut" outlined>{{ $t('Logout') }}</v-btn>
        </div>
      </v-app-bar>

      <v-main>
        <v-container fluid>
          <transition name="fade">
            <router-view :key="$route.params.id"></router-view>
          </transition>
        </v-container>
      </v-main>

      <v-footer fixed clipped-left app>
        <v-spacer></v-spacer>
          &copy; {{ year }} {{ websiteName }}
        <v-spacer></v-spacer>
      </v-footer>
    </div>

    <v-container v-else-if="!loggedIn" id="login-container" fill-height fluid>
      <v-row align="center" justify="center">
         <v-col>
           <transition name="fade">
             <router-view></router-view>
           </transition>
         </v-col>
      </v-row>
    </v-container>

    <v-navigation-drawer
      v-model="searchDrawer"
      absolute
      temporary
      right
      width="500"
    >
      <v-list-item>
        <v-list-item-avatar>
          <v-btn icon @click.stop="toggleSearchDrawer()">
            <v-icon>close</v-icon>
          </v-btn>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>
            <v-text-field
              :label="$t('Search')"
              clearable
              @click:clear="onSearchCleared()"
              @keyup="searchContent"
              v-model="searchValue"
            ></v-text-field>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list v-if="lastChangedContent != null">
        <v-subheader v-if="searchedValues && lastChangedContent.length > 0">{{$t('Search results')}}</v-subheader>
        <v-subheader v-else-if="searchedValues && lastChangedContent.length == 0">{{$t('No results')}}</v-subheader>
        <v-subheader v-else>{{$t('Recent changes')}}</v-subheader>
          <v-list-item
            v-for="content in lastChangedContent"
            :key="content._id.$oid"
            link
            :to="content.PageRepeatingContent ? { name: 'editPageAddEditItem', params: { id: content.PageId.$oid, itemId: content._id.$oid }} : { name: 'editPage', params: { id: content.PageId.$oid }}"
          >
              <v-list-item-avatar>
                  <v-icon>{{content.PageRepeatingContent ? "list": "subject"}}</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>
                  {{ content.DisplayLabel }}
                </v-list-item-title>
                <v-list-item-subtitle
                  v-text="content.WebsiteName + (content.WebsiteName == '' ? '' : ' - ') + content.PageName[currentLanguage]">
                </v-list-item-subtitle>
              </v-list-item-content>
          </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </v-app>
</template>

<script>
import auth from '../auth'
import Vue from 'vue'
import request from '../request'
import languageSelector from './shared/LanguageSelector'
import config from '../config'
import helpers from '../helpers'

import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      error: "",
      userIsAdministrator: auth.userIsAdministrator(),
      loggedIn: auth.loggedIn(),
      year: (new Date()).getFullYear(),
      drawer: null,
      loggingOut:false,
      websiteName: config.websiteName,
      logo: config.logoNeg,
      serverUrl: helpers.getServerUrl(),
      searchDrawer: null,
      lastChangedContent: null,
      searchValue:"",
      searchedValues:false,
      searchTimeout: null
    }
  },
  created () {
    auth.onChange = loggedIn => {
      this.loggedIn = loggedIn
      this.userIsAdministrator = auth.userIsAdministrator()

      if(this.loggedIn){
        this.getWebsites()
      }
    }

    if(this.loggedIn){
      this.getWebsites()
    }
  },
  computed: {...mapGetters({
      websites: 'allWebsites',
      selectedWebsite: 'selectedWebsite'
    }),
    currentLanguage:{
      get: function () {
        return Vue.i18n.locale()
      }
    },
    selectedWebsiteSortedPages:{
      get: function() {
        return this.selectedWebsite.Pages.slice().sort(helpers.sortByKey(this.currentLanguage))
      }
    }
  },
  methods: {
    logOut () {
      this.loggingOut = true

      auth.logOut(res => {
        this.loggingOut = false

        if (!res.success) {
          this.error = res.message
        } else {
          this.error = ""
          this.$router.push({ path: 'login' })
        }
      })
    },
    getWebsites(){
      request.get("/websites", null, (res) => {
        if (res.success) {
            this.$store.dispatch('setAllWebsites', res.data)
            if(res.data.length == 1){
              this.setSelectedWebsite(res.data[0]._id)
            }
        }else{
          this.error = res.message
        }
      })
    },
    setSelectedWebsite(value){
      this.$store.dispatch('setSelectedWebsiteById', value)
    },
    getNameFromPageId(id){
      return this.selectedWebsite.Pages.find(p => p._id.$oid === id).Name[this.currentLanguage]
    },
    toggleSearchDrawer(){
      this.searchDrawer = !this.searchDrawer

      if(this.searchDrawer){
        window.scrollTo(0,0);
        this.getLastChangedContent()
      }
    },
    getLastChangedContent(){
      this.searchValue = "";
      this.lastChangedContent = null;

      request.get("/lastchangedcontent", null, (res) => {
        this.searchedValues = false;

        if (res.success) {
            this.lastChangedContent = res.data
        }else{
          this.error = res.message
        }
      })
    },
    searchContent(){
      var scope = this

      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(function(){
          request.get("/search/?searchValue=" + scope.searchValue, null, (res) => {
            scope.searchedValues = true;
            scope.lastChangedContent = null;

            if (res.success) {
              scope.lastChangedContent = res.data
            }else{
              scope.error = res.message
            }
          })

          clearTimeout(this.searchTimeout)
      }, 800);
    },
    onSearchCleared () {
      this.getLastChangedContent();
    }
  },
  components: {
     languageSelector
   }
}
</script>

<style>
  a{
    text-decoration: none;
  }

  #logo{
    margin-top:10px;
  }

  .row{
    margin:0;
  }

  .router-link-active .v-list-item__subtitle{
    font-weight:bold !important;
  }

  #websiteSelect,
  #websiteSelect .input-group__selections,
  #websiteSelect i{
    margin-top:-8px;
  }

  #websiteSelect .input-group__details{
    display:none;
  }

  #languageSelect{
    margin-right:30px;
  }

  .card ul{
    padding-bottom:0;
  }

  .list__tile__content{
    color:#000 !important;
  }

  .fade-enter-active {
    transition-property: opacity;
    transition-duration: .30s;
  }

  .fade-enter-active {
    transition-property: opacity;
    transition-duration: .30s;
  }

  .fade-enter, .fade-leave-active {
    opacity: 0;
  }

  .custom-overview-row{
    border-bottom:1px solid #ddd;
  }

  .custom-overview-row.no-border{
    border-bottom:0px;
  }

  .custom-overview-row:last-child{
    border-bottom:0;
  }

  .custom-overview-row .input-group:not(.no-styling),
  .custom-overview-row h4{
    margin-left:15px;
    margin-top:15px;
    padding-right:30px;
  }

  .custom-overview-row-actions{
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .custom-overview-row-title{
    text-overflow: ellipsis;
    overflow:hidden;
  }

  .textInput{
    border:1px solid #e4e4e4;
    width:100%;
    min-height:30px;
    padding:5px;
    outline: none;
  }

  .textInput:focus{
      outline: none;
  }

  .pageElementLanguageSelectRichText,
  .pageElementLanguageSelect{
    max-width:65px;
    padding:0;
  }

  .add-item-select{
    max-width:300px;
    float:left;
  }

  .sub-pages-container{
    margin-top:-5px;
    margin-left:5px;
    margin-bottom:10px;
  }

  @media only screen and (max-width: 568px) {
    .edit-page-action-button{
      width:100%;
    }

    .edit-page-action-button .btn__content{
      display: inline-block;
      text-overflow: ellipsis;
      overflow: hidden;
      padding-top:8px;
    }

    .edit-page-action-button .btn__content i{
      padding-top:0px;
      margin-top:-3px;
      margin-left:5px;
    }
  }

  @media only screen and (max-width: 465px) {
    .dropdown-action-button{
      margin:0;
      position:relative;
      top:-14px;
    }
  }
</style>
